import React from 'react';
import { Link, useParams } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import useTheme from '../hooks/useTheme';
import { megaGreenData } from '../data/data';

export const ProjectDetail: React.FC = () => {
    const { theme, breakpoint } = useTheme();
    const { id } = useParams<{ id: string }>();

    // Try to find in caseStudies first, then fall back to projects
    let caseStudy = megaGreenData.projects.caseStudies?.find(cs => cs.id === id);

    if (!caseStudy) {
        // Fall back to projects array
        const project = megaGreenData.projects.projects.find(p => p.id === id);
        if (project) {
            // Convert project to caseStudy format
            const projectId = project.id;
            const projectClient = project.client;
            const fallbackOverview = project.oneSentenceOutcome || `Project ${projectId} for ${projectClient}.`;
            caseStudy = {
                id: project.id,
                client: project.client,
                projectType: project.projectType,
                sector: project.sector,
                location: project.location,
                primaryServiceLine: project.primaryServiceLine,
                overview: fallbackOverview as any,
                scopeApproach: [],
                deliverables: [],
                valueDelivered: project.oneSentenceOutcome || 'Project completed successfully.',
            } as any;
        }
    }

    if (!caseStudy) {
        return (
            <div style={{ padding: '48px', textAlign: 'center' }}>
                <h1>Project not found</h1>
                <Link to="/projects">Back to Projects</Link>
            </div>
        );
    }

    // Handle different property names
    const scopeApproach = (caseStudy as any).scopeApproach || (caseStudy as any).scopeOfWork || [];
    const deliverables = (caseStudy as any).deliverables || (caseStudy as any).keyDeliverables || [];

    const relatedProjects = megaGreenData.projects.projects
        .filter(p =>
            (p.sector === caseStudy.sector || p.primaryServiceLine === caseStudy.primaryServiceLine) &&
            p.id !== caseStudy.id
        )
        .slice(0, 4);

    return (
        <>
            <PageHero
                title={`${caseStudy.client} — ${caseStudy.id}`}
                subtitle={`${caseStudy.projectType} • ${caseStudy.sector} • ${caseStudy.location} • ${caseStudy.primaryServiceLine}`}
            />

            <Section>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : '2fr 1fr',
                        gap: '48px',
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: 'flex',
                                gap: '16px',
                                flexWrap: 'wrap',
                                marginBottom: '32px',
                            }}
                        >
                            <Link to="/contact" style={{ textDecoration: 'none' }}>
                                <Button variant="primary">Request a Consultation</Button>
                            </Link>
                            <Link to="/projects" style={{ textDecoration: 'none' }}>
                                <Button variant="secondary">Back to Projects</Button>
                            </Link>
                        </div>

                        {/* <PlaceholderImage
                            label="PROJECT HERO IMAGE"
                            height={300}
                            style={{ marginBottom: '32px' }}
                        /> */}

                        <h2
                            style={{
                                fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                                fontWeight: 700,
                                color: theme.text.primary,
                                marginBottom: '16px',
                            }}
                        >
                            Overview
                        </h2>
                        <p
                            style={{
                                fontSize: '16px',
                                color: theme.text.muted,
                                lineHeight: 1.6,
                                marginBottom: '32px',
                            }}
                        >
                            {caseStudy.overview}
                        </p>

                        <h2
                            style={{
                                fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                                fontWeight: 700,
                                color: theme.text.primary,
                                marginBottom: '16px',
                            }}
                        >
                            Scope & approach
                        </h2>
                        <ul
                            style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: '0 0 32px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                            }}
                        >
                            {scopeApproach.map((item: string, idx: number) => (
                                <li
                                    key={idx}
                                    style={{
                                        fontSize: '16px',
                                        color: theme.text.muted,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                    }}
                                >
                                    <span style={{ color: theme.palette.primary.main, marginTop: '4px', flexShrink: 0 }}>•</span>
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <h2
                            style={{
                                fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                                fontWeight: 700,
                                color: theme.text.primary,
                                marginBottom: '16px',
                            }}
                        >
                            Deliverables
                        </h2>
                        <ul
                            style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: '0 0 32px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                            }}
                        >
                            {deliverables.map((item: string, idx: number) => (
                                <li
                                    key={idx}
                                    style={{
                                        fontSize: '16px',
                                        color: theme.text.muted,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                    }}
                                >
                                    <span style={{ color: theme.palette.primary.main, marginTop: '4px', flexShrink: 0 }}>•</span>
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <h2
                            style={{
                                fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                                fontWeight: 700,
                                color: theme.text.primary,
                                marginBottom: '16px',
                            }}
                        >
                            Value delivered
                        </h2>
                        <p
                            style={{
                                fontSize: '16px',
                                color: theme.text.muted,
                                lineHeight: 1.6,
                            }}
                        >
                            {caseStudy.valueDelivered}
                        </p>
                    </div>

                    <div>
                        <Card>
                            <h3
                                style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: theme.text.primary,
                                    marginBottom: '20px',
                                }}
                            >
                                Key Facts
                            </h3>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: theme.text.muted,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        Project ID
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: theme.text.primary,
                                        }}
                                    >
                                        {caseStudy.id}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: theme.text.muted,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        Client
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '16px',
                                            color: theme.text.primary,
                                        }}
                                    >
                                        {caseStudy.client}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: theme.text.muted,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        Sector
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '16px',
                                            color: theme.text.primary,
                                        }}
                                    >
                                        {caseStudy.sector}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: theme.text.muted,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        Location
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '16px',
                                            color: theme.text.primary,
                                        }}
                                    >
                                        {caseStudy.location}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: theme.text.muted,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        Service Line
                                    </div>
                                    <div
                                        style={{
                                            fontSize: '16px',
                                            color: theme.text.primary,
                                        }}
                                    >
                                        {caseStudy.primaryServiceLine}
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${theme.colors.line}` }}>
                                <Button variant="secondary" style={{ width: '100%' }}>
                                    Download PDF (Placeholder)
                                </Button>
                            </div>
                        </Card>
                    </div>
                </div>
            </Section>

            {relatedProjects.length > 0 && (
                <Section
                    style={{
                        backgroundColor: theme.background.panel,
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '32px',
                        }}
                    >
                        Related projects
                    </h2>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : breakpoint.md ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                            gap: '24px',
                        }}
                    >
                        {relatedProjects.map((project) => (
                            <Card key={project.id} hoverable>
                                {/* <PlaceholderImage
                                    label="PROJECT THUMBNAIL"
                                    height={120}
                                    style={{ marginBottom: '12px', borderRadius: theme.borderRadius.r1 }}
                                /> */}
                                <div
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {project.id}
                                </div>
                                <h3
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: theme.text.primary,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {project.client}
                                </h3>
                                <p
                                    style={{
                                        fontSize: '13px',
                                        color: theme.text.muted,
                                        marginBottom: '12px',
                                    }}
                                >
                                    {project.location}
                                </p>
                                <Link to={`/projects/${project.id}`} style={{ textDecoration: 'none' }}>
                                    <Button variant="secondary" style={{ width: '100%' }}>
                                        View case study
                                    </Button>
                                </Link>
                            </Card>
                        ))}
                    </div>
                </Section>
            )}

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '16px',
                        }}
                    >
                        Tell us what you're planning
                    </h2>
                    <p
                        style={{
                            fontSize: '16px',
                            color: theme.text.muted,
                            marginBottom: '24px',
                        }}
                    >
                        {megaGreenData.contact.pageIntro}
                    </p>
                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                        <Button variant="primary" style={{ padding: '12px 32px' }}>
                            Request a Consultation
                        </Button>
                    </Link>
                </div>
            </Section>
        </>
    );
};

